// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import type {CurrentSubscriptionPrice, PriceIds} from '@app/features/premium/commands/PremiumCommands';
import * as PremiumCommands from '@app/features/premium/commands/PremiumCommands';
import {formatMinorUnitPrice, type PricingMode} from '@app/features/premium/utils/PricingUtils';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import Users from '@app/features/user/state/Users';
import * as LocaleUtils from '@app/features/user/utils/LocaleUtils';
import {useLingui} from '@lingui/react/macro';
import {useEffect, useMemo, useState} from 'react';

const logger = new Logger('usePremiumData');
const PRICE_PLACEHOLDER = '...';
export interface PremiumData {
	priceIds: PriceIds | null;
	monthlyPrice: string;
	yearlyPrice: string;
	giftMonthlyPrice: string;
	giftYearlyPrice: string;
	localizedPriceIds: PriceIds | null;
	basePriceIds: PriceIds | null;
	hasPricingChoice: boolean;
	selectedPricingMode: PricingMode;
	localizedCurrency: string | null;
	baseCurrency: string | null;
	currentSubscriptionPrice: CurrentSubscriptionPrice | null;
	currentSubscriptionPriceLabel: string | null;
	currentSubscriptionListPriceLabel: string | null;
	isCurrentSubscriptionGrandfathered: boolean;
}
export const usePremiumData = (
	countryCode: string | null,
	pricingMode: PricingMode = 'localized',
	{
		showErrorToast = false,
	}: {
		showErrorToast?: boolean;
	} = {},
): PremiumData => {
	const {t} = useLingui();
	const [localizedPriceIds, setLocalizedPriceIds] = useState<PriceIds | null>(null);
	const [basePriceIds, setBasePriceIds] = useState<PriceIds | null>(null);
	const locale = LocaleUtils.getCurrentLocale();
	useEffect(() => {
		let mounted = true;
		const fetchData = async () => {
			try {
				const prices = await PremiumCommands.fetchPriceIds(countryCode ?? undefined, 'localized');
				if (!mounted) return;
				setLocalizedPriceIds(prices);
			} catch (error) {
				logger.error('Failed to fetch premium data', error);
				if (!mounted) return;
				if (showErrorToast) {
					ToastCommands.error(t`Failed to load premium information. Please try again later.`);
				}
			}
		};
		fetchData();
		return () => {
			mounted = false;
		};
	}, [countryCode, showErrorToast, t]);
	useEffect(() => {
		let mounted = true;
		const fetchData = async () => {
			try {
				const prices = await PremiumCommands.fetchPriceIds(countryCode ?? undefined, 'base');
				if (!mounted) return;
				setBasePriceIds(prices);
			} catch (error) {
				logger.warn('Failed to fetch base premium data', error);
				if (!mounted) return;
				if (showErrorToast && pricingMode === 'base') {
					ToastCommands.error(t`Failed to load standard USD/EUR pricing. Please try again later.`);
				}
			}
		};
		fetchData();
		return () => {
			mounted = false;
		};
	}, [countryCode, pricingMode, showErrorToast, t]);
	const hasPricingChoice =
		typeof localizedPriceIds?.currency === 'string' &&
		typeof basePriceIds?.currency === 'string' &&
		localizedPriceIds.currency !== basePriceIds.currency;
	const priceIds = pricingMode === 'base' && hasPricingChoice ? basePriceIds : localizedPriceIds;
	const monthlyPrice = useMemo(() => {
		return formatMinorUnitPrice(priceIds?.monthly_amount_minor, priceIds?.currency, locale) ?? PRICE_PLACEHOLDER;
	}, [locale, priceIds?.currency, priceIds?.monthly_amount_minor]);
	const yearlyPrice = useMemo(() => {
		return formatMinorUnitPrice(priceIds?.yearly_amount_minor, priceIds?.currency, locale) ?? PRICE_PLACEHOLDER;
	}, [locale, priceIds?.currency, priceIds?.yearly_amount_minor]);
	const giftMonthlyPrice = useMemo(() => {
		return (
			formatMinorUnitPrice(priceIds?.gift_1_month_amount_minor, priceIds?.gift_currency, locale) ?? PRICE_PLACEHOLDER
		);
	}, [locale, priceIds?.gift_1_month_amount_minor, priceIds?.gift_currency]);
	const giftYearlyPrice = useMemo(() => {
		return (
			formatMinorUnitPrice(priceIds?.gift_1_year_amount_minor, priceIds?.gift_currency, locale) ?? PRICE_PLACEHOLDER
		);
	}, [locale, priceIds?.gift_1_year_amount_minor, priceIds?.gift_currency]);
	const [currentSubscriptionPrice, setCurrentSubscriptionPrice] = useState<CurrentSubscriptionPrice | null>(null);
	const currentUserId = Users.currentUser?.id ?? null;
	const hasStripeSubscription = Boolean(Users.currentUser?.hasEverPurchased);
	useEffect(() => {
		if (!currentUserId || !hasStripeSubscription) {
			setCurrentSubscriptionPrice(null);
			return;
		}
		let mounted = true;
		const fetchData = async () => {
			try {
				const price = await PremiumCommands.fetchCurrentSubscriptionPrice();
				if (!mounted) return;
				setCurrentSubscriptionPrice(price);
			} catch (error) {
				logger.warn('Failed to fetch current subscription price', error);
				if (!mounted) return;
				setCurrentSubscriptionPrice(null);
			}
		};
		fetchData();
		return () => {
			mounted = false;
		};
	}, [currentUserId, hasStripeSubscription]);
	const currentSubscriptionPriceLabel = useMemo(() => {
		if (!currentSubscriptionPrice) return null;
		return (
			formatMinorUnitPrice(currentSubscriptionPrice.amount_minor, currentSubscriptionPrice.currency, locale) ?? null
		);
	}, [currentSubscriptionPrice, locale]);
	const currentSubscriptionListPriceLabel = useMemo(() => {
		if (!currentSubscriptionPrice || currentSubscriptionPrice.list_amount_minor == null) return null;
		return (
			formatMinorUnitPrice(currentSubscriptionPrice.list_amount_minor, currentSubscriptionPrice.currency, locale) ??
			null
		);
	}, [currentSubscriptionPrice, locale]);
	return {
		priceIds,
		monthlyPrice,
		yearlyPrice,
		giftMonthlyPrice,
		giftYearlyPrice,
		localizedPriceIds,
		basePriceIds,
		hasPricingChoice,
		selectedPricingMode: pricingMode,
		localizedCurrency: localizedPriceIds?.currency ?? null,
		baseCurrency: hasPricingChoice ? (basePriceIds?.currency ?? null) : null,
		currentSubscriptionPrice,
		currentSubscriptionPriceLabel,
		currentSubscriptionListPriceLabel,
		isCurrentSubscriptionGrandfathered: currentSubscriptionPrice?.is_grandfathered ?? false,
	};
};
